// packages/vigilo-app/src/data/mock.ts
// Rich mock dataset for UI development and demo preparation.
// All data is internally consistent: occupancy counts, capacity utilisation,
// alert trigger context, and flag activity reference the same zone IDs.
//
// Zone IDs match simulator/src/zones.js exactly.
// No PII — flag tokens are UUIDs, mock users have display names only.

import type {
  Zone, DensitySnapshot, Alert, FlagRecord, FlagActivity,
  CommandDashboard, ZoneStatusCard, User, Operation, Venue, VenueType, ZoneType,
} from '@vigilo/shared';

// ─── Time anchor ──────────────────────────────────────────────────────────────
// All timestamps derived from this point so elapsed timers always display
// realistic values regardless of when the module is imported.

const NOW = Date.now();
const ago = (ms: number) => new Date(NOW - ms).toISOString();
const MIN = 60_000;

// ─── Zone IDs ─────────────────────────────────────────────────────────────────

export const ZONE_ID = {
  MGE: '11111111-0000-4000-8000-000000000001',
  NST: '11111111-0000-4000-8000-000000000002',
  SST: '11111111-0000-4000-8000-000000000003',
  MRC: '11111111-0000-4000-8000-000000000004',
  EEC: '11111111-0000-4000-8000-000000000005',
  VIP: '11111111-0000-4000-8000-000000000006',
} as const;

// ─── User IDs ─────────────────────────────────────────────────────────────────

const UID = {
  reyes:     'usr-0001',
  patel:     'usr-0002',
  chen:      'usr-0003',
  halberg:   'usr-0004',
  ostrowski: 'usr-0005',
} as const;

// ─── Venue IDs ────────────────────────────────────────────────────────────────

export const VENUE_ID = {
  ARENA:   '22222222-0000-4000-8000-000000000001',
  CENTRAL: '22222222-0000-4000-8000-000000000002',
} as const;

const TEAM_ID = {
  ALPHA:   '44444444-0000-4000-8000-000000000001',
  BRAVO:   '44444444-0000-4000-8000-000000000002',
  CHARLIE: '44444444-0000-4000-8000-000000000003',
} as const;

// ─── Operation context ────────────────────────────────────────────────────────

const TODAY_1330 = new Date(new Date(NOW).setHours(13, 30, 0, 0)).toISOString();
const TODAY_14   = new Date(new Date(NOW).setHours(14,  0, 0, 0)).toISOString();
const TODAY_22   = new Date(new Date(NOW).setHours(22,  0, 0, 0)).toISOString();
const TMRW_14    = new Date(new Date(NOW + 86_400_000).setHours(14, 0, 0, 0)).toISOString();
const TMRW_22    = new Date(new Date(NOW + 86_400_000).setHours(22, 0, 0, 0)).toISOString();
const YDAY_11    = new Date(new Date(NOW - 86_400_000).setHours(11, 0, 0, 0)).toISOString();
const YDAY_16    = new Date(new Date(NOW - 86_400_000).setHours(16, 0, 0, 0)).toISOString();
const YDAY_21    = new Date(new Date(NOW - 86_400_000).setHours(21, 0, 0, 0)).toISOString();

const ALL_ZONE_IDS = Object.values(ZONE_ID) as string[];

export const MOCK_OPERATIONS: Operation[] = [
  {
    id:              '33333333-0000-4000-8000-000000000001',
    name:            'UEFA Champions League — Group Stage',
    venueId:         VENUE_ID.ARENA,
    eventType:       'sports_match',
    status:          'active',
    scheduledStart:  TODAY_14,
    scheduledEnd:    TODAY_22,
    actualStart:     TODAY_1330,
    actualEnd:       null,
    assignedZoneIds: ALL_ZONE_IDS,
    assignedTeams:   ['Alpha Team', 'Bravo Team', 'Charlie Team'],
    defaultTeamId:   TEAM_ID.ALPHA,
    zoneTeamAssignments: [
      { zoneId: ZONE_ID.MGE, teamId: TEAM_ID.ALPHA, inheritedFromDefault: true },
      { zoneId: ZONE_ID.NST, teamId: TEAM_ID.BRAVO, inheritedFromDefault: false },
      { zoneId: ZONE_ID.SST, teamId: TEAM_ID.CHARLIE, inheritedFromDefault: false },
      { zoneId: ZONE_ID.MRC, teamId: TEAM_ID.ALPHA, inheritedFromDefault: true },
      { zoneId: ZONE_ID.EEC, teamId: TEAM_ID.ALPHA, inheritedFromDefault: true },
      { zoneId: ZONE_ID.VIP, teamId: TEAM_ID.ALPHA, inheritedFromDefault: true },
    ],
    createdBy:       'usr-0005',
    closedBy:        null,
    createdAt:       ago(180 * MIN),
    updatedAt:       ago(90 * MIN),
  },
  {
    id:              '33333333-0000-4000-8000-000000000002',
    name:            'UEFA CL — Knockout Stage',
    venueId:         VENUE_ID.ARENA,
    eventType:       'sports_match',
    status:          'scheduled',
    scheduledStart:  TMRW_14,
    scheduledEnd:    TMRW_22,
    actualStart:     null,
    actualEnd:       null,
    assignedZoneIds: ALL_ZONE_IDS,
    assignedTeams:   ['Alpha Team', 'Bravo Team'],
    defaultTeamId:   TEAM_ID.ALPHA,
    zoneTeamAssignments: ALL_ZONE_IDS.map(zoneId => ({
      zoneId,
      teamId: zoneId === ZONE_ID.NST ? TEAM_ID.BRAVO : TEAM_ID.ALPHA,
      inheritedFromDefault: zoneId !== ZONE_ID.NST,
    })),
    createdBy:       'usr-0005',
    closedBy:        null,
    createdAt:       ago(60 * MIN),
    updatedAt:       ago(60 * MIN),
  },
  {
    id:              '33333333-0000-4000-8000-000000000003',
    name:            'Amsterdam Centraal - Evening Flow',
    venueId:         VENUE_ID.CENTRAL,
    eventType:       'transport_hub',
    status:          'paused',
    scheduledStart:  TODAY_14,
    scheduledEnd:    TODAY_22,
    actualStart:     TODAY_1330,
    actualEnd:       null,
    assignedZoneIds: [ZONE_ID.MGE, ZONE_ID.MRC, ZONE_ID.EEC],
    assignedTeams:   ['Delta Team', 'Command'],
    defaultTeamId:   null,
    zoneTeamAssignments: [],
    createdBy:       'usr-0005',
    closedBy:        null,
    createdAt:       ago(150 * MIN),
    updatedAt:       ago(20 * MIN),
  },
  {
    id:              '33333333-0000-4000-8000-000000000004',
    name:            'Arena Concert - Post Event Debrief',
    venueId:         VENUE_ID.ARENA,
    eventType:       'concert',
    status:          'closed',
    scheduledStart:  YDAY_11,
    scheduledEnd:    YDAY_21,
    actualStart:     YDAY_11,
    actualEnd:       YDAY_21,
    assignedZoneIds: ALL_ZONE_IDS,
    assignedTeams:   ['Alpha Team', 'Bravo Team', 'Command'],
    defaultTeamId:   TEAM_ID.ALPHA,
    zoneTeamAssignments: ALL_ZONE_IDS.map(zoneId => ({
      zoneId,
      teamId: zoneId === ZONE_ID.NST ? TEAM_ID.BRAVO : TEAM_ID.ALPHA,
      inheritedFromDefault: zoneId !== ZONE_ID.NST,
    })),
    createdBy:       'usr-0005',
    closedBy:        'usr-0004',
    createdAt:       ago(32 * 60 * MIN),
    updatedAt:       ago(24 * 60 * MIN),
  },
  {
    id:              '33333333-0000-4000-8000-000000000005',
    name:            'Amsterdam Centraal - Morning Peak Review',
    venueId:         VENUE_ID.CENTRAL,
    eventType:       'transport_hub',
    status:          'closed',
    scheduledStart:  YDAY_11,
    scheduledEnd:    YDAY_16,
    actualStart:     YDAY_11,
    actualEnd:       YDAY_16,
    assignedZoneIds: [ZONE_ID.MGE, ZONE_ID.EEC],
    assignedTeams:   ['Delta Team'],
    defaultTeamId:   null,
    zoneTeamAssignments: [],
    createdBy:       'usr-0005',
    closedBy:        'usr-0004',
    createdAt:       ago(35 * 60 * MIN),
    updatedAt:       ago(30 * 60 * MIN),
  },
];

// Legacy single-op reference — remove when all screens use MOCK_OPERATIONS.
export const MOCK_OPERATION = {
  name:      MOCK_OPERATIONS[0].name,
  venue:     'Johan Cruijff ArenA',
  date:      new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(MOCK_OPERATIONS[0].scheduledStart)),
  eventType: 'Football Match',
} as const;

// ─── Venues ───────────────────────────────────────────────────────────────────

export const MOCK_VENUES: Venue[] = [
  {
    id:            VENUE_ID.ARENA,
    name:          'Johan Cruijff ArenA',
    venueType:     'stadium',
    city:          'Amsterdam',
    country:       'NL',
    address:       'Arena Boulevard 1, 1101 AX Amsterdam',
    totalCapacity: 54990,
    geometry:      null,
    createdBy:     'usr-0005',
    archivedAt:    null,
    createdAt:     ago(365 * 24 * 60 * MIN),
    updatedAt:     ago(30 * 24 * 60 * MIN),
  },
  {
    id:            VENUE_ID.CENTRAL,
    name:          'Amsterdam Centraal',
    venueType:     'transport_hub',
    city:          'Amsterdam',
    country:       'NL',
    address:       'Stationsplein, 1012 AB Amsterdam',
    totalCapacity: 250000,
    geometry:      null,
    createdBy:     'usr-0005',
    archivedAt:    null,
    createdAt:     ago(365 * 24 * 60 * MIN),
    updatedAt:     ago(30 * 24 * 60 * MIN),
  },
];

export interface MockCrowdTaxonomyItem {
  code: string;
  label: string;
  shortCode: string | null;
  description: string | null;
  sortOrder: number;
}

export interface MockCrowdZonePreset {
  zoneType: ZoneType;
  label: string;
  description: string | null;
  icon: string | null;
  sortOrder: number;
  defaultThresholds: Record<string, number>;
  rules: unknown[];
}

export interface MockCrowdTaxonomy {
  applicationId: 'crowd_monitoring';
  venueTypes: Array<MockCrowdTaxonomyItem & { code: VenueType }>;
  zoneTypes: Array<MockCrowdTaxonomyItem & { code: ZoneType }>;
  operationEventTypes: MockCrowdTaxonomyItem[];
  zonePresets: MockCrowdZonePreset[];
}

export interface MockAdminDataFeeds {
  applicationId: 'crowd_monitoring';
  generatedAt: string;
  feeds: Array<{
    id: string;
    label: string;
    boundary: string;
    status: 'configured' | 'not_configured' | 'standby';
    detail: string;
    governance: 'anonymous_only';
    allowedOutputs: string[];
  }>;
}

export interface MockAdminAuditLog {
  applicationId: 'crowd_monitoring';
  generatedAt: string;
  entries: Array<{
    id: string;
    occurredAt: string;
    actor: string;
    actorRole: string | null;
    actorType: 'human' | 'service_account' | 'system_cron' | 'anonymous';
    actorRoleSnapshot: Record<string, unknown>;
    action: string;
    resourceType: string;
    resourceId: string;
    outcome: 'success' | 'failure' | 'denied';
    correlationId: string | null;
    sessionId: string | null;
    sourceIpHash: string | null;
    userAgentHash: string | null;
    userAgentFamily: string | null;
    clientType: string | null;
    operationId: string | null;
    httpMethod: string | null;
    endpointPath: string | null;
    statusCode: number | null;
    queryParams: Record<string, unknown>;
    previousState: Record<string, unknown> | null;
    newState: Record<string, unknown> | null;
    failureReasonCode: string | null;
    failureReasonDetail: string | null;
    hashAlgorithm: string | null;
    previousRowHash: string | null;
    rowHash: string | null;
    latestReview: {
      id: string;
      auditLogId: string;
      reviewAction: 'reviewed' | 'flagged' | 'unflagged' | 'commented';
      reviewedBy: string;
      reviewedAt: string;
      reviewerRoleSnapshot: Record<string, unknown>;
    } | null;
    synthetic: boolean;
    target: string;
    detail: string;
  }>;
  page: number;
  pageSize: number;
  total: number;
  hasNextPage: boolean;
  filters: {
    page: number;
    pageSize: number;
  };
  filterOptions: {
    actions: string[];
    resourceTypes: string[];
    outcomes: Array<'success' | 'failure' | 'denied'>;
    actorRoles: string[];
    actorTypes: string[];
    httpMethods: string[];
  };
}

export const MOCK_ADMIN_DATA_FEEDS: MockAdminDataFeeds = {
  applicationId: 'crowd_monitoring',
  generatedAt: new Date().toISOString(),
  feeds: [
    {
      id: 'core-event-aggregate-stream',
      label: 'Core Event Aggregate Stream',
      boundary: 'Core Event API -> Crowd live projection',
      status: 'configured',
      detail: 'Real-time anonymous density, flow, composition, and anomaly inputs.',
      governance: 'anonymous_only',
      allowedOutputs: ['DensitySnapshot', 'FlowSnapshot', 'ZoneComposition', 'AnomalyAlert'],
    },
    {
      id: 'historical-aggregate-batches',
      label: 'Historical Aggregate Batches',
      boundary: 'Approved analytics output -> Core Timeline API',
      status: 'not_configured',
      detail: 'Redacted historical density and zone-intelligence summaries only.',
      governance: 'anonymous_only',
      allowedOutputs: ['HistoricalDensitySeries(redacted)', 'ZoneIntelligenceSummary(redacted)'],
    },
    {
      id: 'manual-operational-inputs',
      label: 'Manual Operational Inputs',
      boundary: 'Crowd Admin UI -> Core Event API',
      status: 'configured',
      detail: 'Operator-entered operational notes and setup events through approved Core event ingestion.',
      governance: 'anonymous_only',
      allowedOutputs: ['DensitySnapshot', 'FlowSnapshot', 'ZoneComposition'],
    },
    {
      id: 'simulation-producer',
      label: 'Simulation Producer',
      boundary: 'Simulation service -> Core Event API -> Crowd projection',
      status: 'standby',
      detail: 'Synthetic producer matching live aggregate contracts for test and demo environments.',
      governance: 'anonymous_only',
      allowedOutputs: ['DensitySnapshot', 'FlowSnapshot', 'ZoneComposition', 'AnomalyAlert'],
    },
  ],
};

export const MOCK_ADMIN_AUDIT_LOG: MockAdminAuditLog = {
  applicationId: 'crowd_monitoring',
  generatedAt: new Date().toISOString(),
  page: 1,
  pageSize: 25,
  total: 9,
  hasNextPage: false,
  filters: { page: 1, pageSize: 25 },
  filterOptions: {
    actions: [
      'core.auth.login_success',
      'crowd.alert_rule.created',
      'crowd.alert_rule.deleted',
      'crowd.alert_rule.updated',
      'crowd.alert.acknowledged',
      'crowd.alert.assigned',
      'crowd.alert.escalated',
      'crowd.alert.noted',
      'crowd.alert.resolved',
      'crowd.operation.activated',
      'crowd.operation.closed',
      'crowd.operation.created',
      'crowd.operation.paused',
      'crowd.operation.resumed',
      'crowd.operation.updated',
      'crowd.report.deleted',
      'crowd.report.downloaded',
      'crowd.report.requested',
      'crowd.team.archived',
      'crowd.team.created',
      'crowd.team.updated',
      'crowd.venue.archived',
      'crowd.venue.created',
      'crowd.venue.updated',
      'crowd.zone.archived',
      'crowd.zone.created',
      'user.activate',
      'user.create',
      'user.deactivate',
      'user.update',
    ],
    resourceTypes: ['core.session', 'core.user', 'crowd.alert', 'crowd.alert_rule', 'crowd.operation', 'crowd.report', 'crowd.team', 'crowd.venue', 'crowd.zone'],
    outcomes: ['success', 'failure', 'denied'],
    actorRoles: ['command_supervisor', 'crowd_analyst', 'system_admin'],
    actorTypes: ['anonymous', 'human', 'service_account', 'system_cron'],
    httpMethods: ['CLIENT', 'DELETE', 'GET', 'PATCH', 'POST', 'PUT'],
  },
  entries: [
    {
      id: 'audit-mock-operation-activated',
      occurredAt: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
      actor: 'system-admin',
      actorRole: 'system_admin',
      actorType: 'human',
      actorRoleSnapshot: { role: 'system_admin' },
      action: 'crowd.operation.activated',
      resourceType: 'crowd.operation',
      resourceId: MOCK_OPERATIONS[0].id,
      outcome: 'success',
      correlationId: 'req-mock-1001',
      sessionId: 'mock-session',
      sourceIpHash: 'sha256:7b1e2e4f8a9c11d2',
      userAgentHash: 'sha256:6de0b1a8ce5d9940',
      userAgentFamily: 'chromium',
      clientType: 'browser',
      operationId: MOCK_OPERATIONS[0].id,
      httpMethod: 'POST',
      endpointPath: '/api/v1/operations/:id/activate',
      statusCode: 200,
      queryParams: { operationId: MOCK_OPERATIONS[0].id },
      previousState: null,
      newState: {
        operationId: MOCK_OPERATIONS[0].id,
        sessionId: 'mock-session',
        status: 'active',
        monitoredZoneCount: 6,
      },
      failureReasonCode: null,
      failureReasonDetail: null,
      hashAlgorithm: 'sha256',
      previousRowHash: '0b7f0b3c8d594d9a9c0e7a65d4a1490d64a52fd098bc88a43fd19981cecb1002',
      rowHash: '0b7f0b3c8d594d9a9c0e7a65d4a1490d64a52fd098bc88a43fd19981cecb1003',
      latestReview: null,
      synthetic: true,
      target: 'Crowd Operation 33333333',
      detail: 'Operation Activated - operation op-demo - status active - 6 monitored zones',
    },
    {
      id: 'audit-mock-venue-created',
      occurredAt: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
      actor: 'command-supervisor',
      actorRole: 'command_supervisor',
      actorType: 'human',
      actorRoleSnapshot: { role: 'command_supervisor' },
      action: 'crowd.venue.created',
      resourceType: 'crowd.venue',
      resourceId: MOCK_VENUES[0].id,
      outcome: 'success',
      correlationId: 'req-mock-1004',
      sessionId: 'mock-session',
      sourceIpHash: 'sha256:7b1e2e4f8a9c11d2',
      userAgentHash: 'sha256:6de0b1a8ce5d9940',
      userAgentFamily: 'chromium',
      clientType: 'browser',
      operationId: null,
      httpMethod: 'POST',
      endpointPath: '/api/v1/venues',
      statusCode: 201,
      queryParams: {},
      previousState: null,
      newState: { status: 'active' },
      failureReasonCode: null,
      failureReasonDetail: null,
      hashAlgorithm: 'sha256',
      previousRowHash: null,
      rowHash: '0b7f0b3c8d594d9a9c0e7a65d4a1490d64a52fd098bc88a43fd19981cecb1004',
      latestReview: null,
      synthetic: true,
      target: 'Crowd Venue venue-01',
      detail: 'Venue Created - transition created - status active',
    },
    {
      id: 'audit-mock-zone-archived',
      occurredAt: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
      actor: 'system-admin',
      actorRole: 'system_admin',
      actorType: 'human',
      actorRoleSnapshot: { role: 'system_admin' },
      action: 'crowd.zone.archived',
      resourceType: 'crowd.zone',
      resourceId: MOCK_OPERATIONS[0].assignedZoneIds[0],
      outcome: 'success',
      correlationId: 'req-mock-1005',
      sessionId: 'mock-session',
      sourceIpHash: 'sha256:7b1e2e4f8a9c11d2',
      userAgentHash: 'sha256:6de0b1a8ce5d9940',
      userAgentFamily: 'chromium',
      clientType: 'browser',
      operationId: null,
      httpMethod: 'POST',
      endpointPath: '/api/v1/crowd/zones/:id/archive',
      statusCode: 200,
      queryParams: {},
      previousState: { status: 'active' },
      newState: { status: 'archived' },
      failureReasonCode: null,
      failureReasonDetail: null,
      hashAlgorithm: 'sha256',
      previousRowHash: null,
      rowHash: '0b7f0b3c8d594d9a9c0e7a65d4a1490d64a52fd098bc88a43fd19981cecb1005',
      latestReview: null,
      synthetic: true,
      target: 'Crowd Zone zone-01',
      detail: 'Zone Archived - transition archived - status archived',
    },
    {
      id: 'audit-mock-report-requested',
      occurredAt: new Date(Date.now() - 13 * 60 * 1000).toISOString(),
      actor: 'crowd-analyst',
      actorRole: 'crowd_analyst',
      actorType: 'human',
      actorRoleSnapshot: { role: 'crowd_analyst' },
      action: 'crowd.report.requested',
      resourceType: 'crowd.report',
      resourceId: 'report-job-demo',
      outcome: 'success',
      correlationId: 'req-mock-1006',
      sessionId: 'mock-session',
      sourceIpHash: 'sha256:7b1e2e4f8a9c11d2',
      userAgentHash: 'sha256:6de0b1a8ce5d9940',
      userAgentFamily: 'chromium',
      clientType: 'browser',
      operationId: MOCK_OPERATIONS[0].id,
      httpMethod: 'POST',
      endpointPath: '/api/v1/reports',
      statusCode: 201,
      queryParams: { operationId: MOCK_OPERATIONS[0].id },
      previousState: null,
      newState: { status: 'queued', templateId: 'operational_summary', format: 'pdf' },
      failureReasonCode: null,
      failureReasonDetail: null,
      hashAlgorithm: 'sha256',
      previousRowHash: null,
      rowHash: '0b7f0b3c8d594d9a9c0e7a65d4a1490d64a52fd098bc88a43fd19981cecb1006',
      latestReview: null,
      synthetic: true,
      target: 'Crowd Report report-j',
      detail: 'Report Requested - operation op-demo - template operational_summary - format pdf',
    },
    {
      id: 'audit-mock-user-created',
      occurredAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
      actor: 'system-admin',
      actorRole: 'system_admin',
      actorType: 'human',
      actorRoleSnapshot: { role: 'system_admin' },
      action: 'user.create',
      resourceType: 'core.user',
      resourceId: 'crowd-user-demo',
      outcome: 'success',
      correlationId: 'req-mock-1007',
      sessionId: 'mock-session',
      sourceIpHash: 'sha256:7b1e2e4f8a9c11d2',
      userAgentHash: 'sha256:6de0b1a8ce5d9940',
      userAgentFamily: 'chromium',
      clientType: 'browser',
      operationId: null,
      httpMethod: 'POST',
      endpointPath: '/api/v1/users',
      statusCode: 201,
      queryParams: {},
      previousState: null,
      newState: { targetRole: 'crowd_analyst' },
      failureReasonCode: null,
      failureReasonDetail: null,
      hashAlgorithm: 'sha256',
      previousRowHash: null,
      rowHash: '0b7f0b3c8d594d9a9c0e7a65d4a1490d64a52fd098bc88a43fd19981cecb1007',
      latestReview: null,
      synthetic: true,
      target: 'Core User crowd-us',
      detail: 'User Create - target role crowd_analyst',
    },
    {
      id: 'audit-mock-team-created',
      occurredAt: new Date(Date.now() - 16 * 60 * 1000).toISOString(),
      actor: 'command-supervisor',
      actorRole: 'command_supervisor',
      actorType: 'human',
      actorRoleSnapshot: { role: 'command_supervisor' },
      action: 'crowd.team.created',
      resourceType: 'crowd.team',
      resourceId: 'team-demo',
      outcome: 'success',
      correlationId: 'req-mock-1008',
      sessionId: 'mock-session',
      sourceIpHash: 'sha256:7b1e2e4f8a9c11d2',
      userAgentHash: 'sha256:6de0b1a8ce5d9940',
      userAgentFamily: 'chromium',
      clientType: 'browser',
      operationId: null,
      httpMethod: 'POST',
      endpointPath: '/api/v1/teams',
      statusCode: 201,
      queryParams: {},
      previousState: null,
      newState: { status: 'active' },
      failureReasonCode: null,
      failureReasonDetail: null,
      hashAlgorithm: 'sha256',
      previousRowHash: null,
      rowHash: '0b7f0b3c8d594d9a9c0e7a65d4a1490d64a52fd098bc88a43fd19981cecb1008',
      latestReview: null,
      synthetic: true,
      target: 'Crowd Team team-dem',
      detail: 'Team Created - transition created - status active',
    },
    {
      id: 'audit-mock-alert-rule-updated',
      occurredAt: new Date(Date.now() - 17 * 60 * 1000).toISOString(),
      actor: 'system-admin',
      actorRole: 'system_admin',
      actorType: 'human',
      actorRoleSnapshot: { role: 'system_admin' },
      action: 'crowd.alert_rule.updated',
      resourceType: 'crowd.alert_rule',
      resourceId: 'rule-demo',
      outcome: 'success',
      correlationId: 'req-mock-1009',
      sessionId: 'mock-session',
      sourceIpHash: 'sha256:7b1e2e4f8a9c11d2',
      userAgentHash: 'sha256:6de0b1a8ce5d9940',
      userAgentFamily: 'chromium',
      clientType: 'browser',
      operationId: null,
      httpMethod: 'PATCH',
      endpointPath: '/api/v1/alerts/rules/:id',
      statusCode: 200,
      queryParams: {},
      previousState: { priority: 'medium' },
      newState: { priority: 'high' },
      failureReasonCode: null,
      failureReasonDetail: null,
      hashAlgorithm: 'sha256',
      previousRowHash: null,
      rowHash: '0b7f0b3c8d594d9a9c0e7a65d4a1490d64a52fd098bc88a43fd19981cecb1009',
      latestReview: null,
      synthetic: true,
      target: 'Crowd Alert Rule rule-dem',
      detail: 'Alert Rule Updated - transition updated',
    },
    {
      id: 'audit-mock-operation-updated',
      occurredAt: new Date(Date.now() - 14 * 60 * 1000).toISOString(),
      actor: 'command-supervisor',
      actorRole: 'command_supervisor',
      actorType: 'human',
      actorRoleSnapshot: { role: 'command_supervisor' },
      action: 'crowd.operation.updated',
      resourceType: 'crowd.operation',
      resourceId: MOCK_OPERATIONS[0].id,
      outcome: 'success',
      correlationId: 'req-mock-1002',
      sessionId: 'mock-session',
      sourceIpHash: 'sha256:7b1e2e4f8a9c11d2',
      userAgentHash: 'sha256:6de0b1a8ce5d9940',
      userAgentFamily: 'chromium',
      clientType: 'browser',
      operationId: MOCK_OPERATIONS[0].id,
      httpMethod: 'PATCH',
      endpointPath: '/api/v1/operations/:id',
      statusCode: 200,
      queryParams: { operationId: MOCK_OPERATIONS[0].id },
      previousState: {
        operationId: MOCK_OPERATIONS[0].id,
        status: 'scheduled',
        monitoredZoneCount: 5,
      },
      newState: {
        operationId: MOCK_OPERATIONS[0].id,
        status: 'active',
        monitoredZoneCount: 6,
      },
      failureReasonCode: null,
      failureReasonDetail: null,
      hashAlgorithm: 'sha256',
      previousRowHash: '0b7f0b3c8d594d9a9c0e7a65d4a1490d64a52fd098bc88a43fd19981cecb1001',
      rowHash: '0b7f0b3c8d594d9a9c0e7a65d4a1490d64a52fd098bc88a43fd19981cecb1002',
      latestReview: {
        id: 'mock-review-flag-update',
        auditLogId: 'audit-mock-operation-updated',
        reviewAction: 'reviewed',
        reviewedBy: 'system-admin',
        reviewedAt: ago(5 * MIN),
        reviewerRoleSnapshot: { role: 'system_admin' },
      },
      synthetic: true,
      target: 'Crowd Operation 33333333',
      detail: 'Operation Updated - operation op-demo - changed status, monitoredZoneCount',
    },
    {
      id: 'audit-mock-operation-closed',
      occurredAt: new Date(Date.now() - 27 * 60 * 1000).toISOString(),
      actor: 'system-admin',
      actorRole: 'system_admin',
      actorType: 'human',
      actorRoleSnapshot: { role: 'system_admin' },
      action: 'crowd.operation.closed',
      resourceType: 'crowd.operation',
      resourceId: MOCK_OPERATIONS[0].id,
      outcome: 'denied',
      correlationId: 'req-mock-1003',
      sessionId: 'mock-session',
      sourceIpHash: 'sha256:2c9504e097f45a12',
      userAgentHash: 'sha256:0d63c8649ff3a17b',
      userAgentFamily: 'api-client',
      clientType: 'script',
      operationId: MOCK_OPERATIONS[0].id,
      httpMethod: 'DELETE',
      endpointPath: '/api/v1/operations/:id/close',
      statusCode: 403,
      queryParams: { operationId: MOCK_OPERATIONS[0].id },
      previousState: {
        operationId: MOCK_OPERATIONS[0].id,
        status: 'active',
        monitoredZoneCount: 6,
      },
      newState: null,
      failureReasonCode: 'auth.forbidden',
      failureReasonDetail: 'role check denied',
      hashAlgorithm: 'sha256',
      previousRowHash: null,
      rowHash: '0b7f0b3c8d594d9a9c0e7a65d4a1490d64a52fd098bc88a43fd19981cecb1001',
      latestReview: {
        id: 'mock-review-flag-archive',
        auditLogId: 'audit-mock-operation-closed',
        reviewAction: 'flagged',
        reviewedBy: 'system-admin',
        reviewedAt: ago(3 * MIN),
        reviewerRoleSnapshot: { role: 'system_admin' },
      },
      synthetic: true,
      target: 'Crowd Operation 33333333',
      detail: 'Operation Closed - operation op-demo - status 403',
    },
  ],
};

export const MOCK_CROWD_TAXONOMY: MockCrowdTaxonomy = {
  applicationId: 'crowd_monitoring',
  venueTypes: [
    { code: 'stadium', label: 'Stadium', shortCode: null, description: null, sortOrder: 10 },
    { code: 'arena', label: 'Arena', shortCode: null, description: null, sortOrder: 20 },
    { code: 'transport_hub', label: 'Transport Hub', shortCode: null, description: null, sortOrder: 30 },
    { code: 'festival_ground', label: 'Festival Ground', shortCode: null, description: null, sortOrder: 40 },
    { code: 'concert_hall', label: 'Concert Hall', shortCode: null, description: null, sortOrder: 50 },
    { code: 'convention_centre', label: 'Convention Centre', shortCode: null, description: null, sortOrder: 60 },
    { code: 'theme_park', label: 'Theme Park', shortCode: null, description: null, sortOrder: 70 },
    { code: 'public_space', label: 'Public Space', shortCode: null, description: null, sortOrder: 80 },
    { code: 'race_circuit', label: 'Race Circuit', shortCode: null, description: null, sortOrder: 90 },
    { code: 'temporary_site', label: 'Temporary Site', shortCode: null, description: null, sortOrder: 100 },
  ],
  zoneTypes: [
    { code: 'entry_exit_gate', label: 'Entry / Exit Gate', shortCode: 'GATE', description: 'High-flow entry, exit, turnstile, and queue-management zone.', sortOrder: 10 },
    { code: 'standing_area', label: 'Standing Area', shortCode: 'STAND', description: 'General admission or standing crowd area.', sortOrder: 20 },
    { code: 'seated_section', label: 'Seated Section', shortCode: 'SEAT', description: 'Tiered or fixed-seat audience area.', sortOrder: 30 },
    { code: 'vip_area', label: 'VIP / Hospitality Area', shortCode: 'VIP', description: 'Restricted hospitality or accreditation-only crowd area.', sortOrder: 40 },
    { code: 'circulation_corridor', label: 'Circulation Corridor', shortCode: 'FLOW', description: 'Walkway, concourse, corridor, or pedestrian flow channel.', sortOrder: 50 },
    { code: 'service_area', label: 'Service Area', shortCode: 'SVC', description: 'Merchandise, concessions, toilets, medical, or support service area.', sortOrder: 60 },
    { code: 'assembly_point', label: 'Assembly Point', shortCode: 'ASM', description: 'Designated congregation, muster, or evacuation assembly location.', sortOrder: 70 },
    { code: 'restricted_zone', label: 'Restricted Zone', shortCode: 'RST', description: 'Access-controlled operational or safety area.', sortOrder: 80 },
    { code: 'perimeter', label: 'Perimeter', shortCode: 'PER', description: 'Outer venue perimeter, fence line, or boundary control area.', sortOrder: 90 },
    { code: 'temporary_ops', label: 'Temporary Ops Zone', shortCode: 'TMP', description: 'Temporary operational zone created for an active Crowd operation.', sortOrder: 100 },
  ],
  operationEventTypes: [
    { code: 'sports_match', label: 'Sports Match', shortCode: null, description: 'Football match, stadium fixture, or sports event operation.', sortOrder: 10 },
    { code: 'concert', label: 'Concert', shortCode: null, description: 'Music, performance, or concert-hall event operation.', sortOrder: 20 },
    { code: 'protest_demonstration', label: 'Protest / Demonstration', shortCode: null, description: 'Public demonstration, protest, or march operation.', sortOrder: 30 },
    { code: 'public_gathering', label: 'Public Gathering', shortCode: null, description: 'General public assembly or crowd gathering operation.', sortOrder: 40 },
    { code: 'vip_event', label: 'VIP Event', shortCode: null, description: 'VIP, dignitary, or high-security public event operation.', sortOrder: 50 },
    { code: 'religious_event', label: 'Religious Event', shortCode: null, description: 'Religious gathering, ceremony, or pilgrimage operation.', sortOrder: 60 },
    { code: 'transport_peak', label: 'Transport Peak', shortCode: null, description: 'Peak-hour or transport-hub crowd monitoring operation.', sortOrder: 70 },
    { code: 'custom', label: 'Custom', shortCode: null, description: 'Other Crowd Monitoring operation type.', sortOrder: 80 },
  ],
  zonePresets: [
    { zoneType: 'entry_exit_gate', label: 'Entry / Exit Gate', description: 'High-flow access point with surge and queue monitoring.', icon: 'log-in', sortOrder: 10, defaultThresholds: { overcrowding: 80, surge: 30, capacityWarning: 70 }, rules: [
      { alertType: 'overcrowding', thresholds: { densityPct: 80 }, priority: 'high' },
      { alertType: 'zone_capacity_warning', thresholds: { densityPct: 70 }, priority: 'medium' },
      { alertType: 'crowd_surge', thresholds: { surgeRatePct: 30, timeWindowMin: 3 }, priority: 'high' },
    ] },
    { zoneType: 'standing_area', label: 'Standing Area', description: 'General admission density and crowd surge monitoring.', icon: 'users', sortOrder: 20, defaultThresholds: { overcrowding: 85, surge: 20, dispersal: 40 }, rules: [
      { alertType: 'overcrowding', thresholds: { densityPct: 85 }, priority: 'high', autoEscalateAfterMin: 3 },
      { alertType: 'crowd_surge', thresholds: { surgeRatePct: 20, timeWindowMin: 5 }, priority: 'high' },
      { alertType: 'sudden_dispersal', thresholds: { surgeRatePct: 40, timeWindowMin: 5 }, priority: 'high' },
    ] },
    { zoneType: 'seated_section', label: 'Seated Section', description: 'Seated audience section with capacity and movement monitoring.', icon: 'armchair', sortOrder: 30, defaultThresholds: { overcrowding: 95, surge: 15, capacityWarning: 85 }, rules: [
      { alertType: 'overcrowding', thresholds: { densityPct: 95 }, priority: 'critical' },
      { alertType: 'zone_capacity_warning', thresholds: { densityPct: 85 }, priority: 'medium' },
      { alertType: 'crowd_surge', thresholds: { surgeRatePct: 15, timeWindowMin: 5 }, priority: 'medium' },
    ] },
    { zoneType: 'vip_area', label: 'VIP / Hospitality Area', description: 'Restricted hospitality zone with access anomaly monitoring.', icon: 'badge-check', sortOrder: 40, defaultThresholds: { overcrowding: 60, reentryWindowHours: 2 }, rules: [
      { alertType: 'overcrowding', thresholds: { densityPct: 60 }, priority: 'high' },
      { alertType: 'restricted_zone_gathering', thresholds: {}, priority: 'critical' },
      { alertType: 'flagged_token_entry', thresholds: {}, priority: 'high' },
      { alertType: 'flagged_token_reentry', thresholds: { reentryWindowHours: 2 }, priority: 'critical' },
    ] },
    { zoneType: 'circulation_corridor', label: 'Circulation Corridor', description: 'Pedestrian flow corridor with congestion monitoring.', icon: 'move-right', sortOrder: 50, defaultThresholds: { thresholdBreach: 60, clusterSize: 10, dwellMinutes: 5 }, rules: [
      { alertType: 'threshold_breach', thresholds: { densityPct: 60 }, priority: 'high' },
      { alertType: 'sustained_loitering_cluster', thresholds: { personCount: 10, dwellMinutes: 5 }, priority: 'medium' },
    ] },
    { zoneType: 'service_area', label: 'Service Area', description: 'Support-service area with queue and dwell monitoring.', icon: 'store', sortOrder: 60, defaultThresholds: { overcrowding: 80, surge: 25 }, rules: [
      { alertType: 'overcrowding', thresholds: { densityPct: 80 }, priority: 'medium' },
      { alertType: 'crowd_surge', thresholds: { surgeRatePct: 25, timeWindowMin: 10 }, priority: 'medium' },
    ] },
    { zoneType: 'assembly_point', label: 'Assembly Point', description: 'Assembly or muster area with capacity monitoring.', icon: 'map-pin', sortOrder: 70, defaultThresholds: {}, rules: [
      { alertType: 'restricted_zone_gathering', thresholds: {}, priority: 'high' },
    ] },
    { zoneType: 'restricted_zone', label: 'Restricted Zone', description: 'Access-controlled zone with gathering detection.', icon: 'shield-alert', sortOrder: 80, defaultThresholds: { autoEscalateAfterMin: 1 }, rules: [
      { alertType: 'restricted_zone_gathering', thresholds: {}, priority: 'critical', autoEscalateAfterMin: 1 },
      { alertType: 'flagged_token_entry', thresholds: {}, priority: 'critical' },
    ] },
    { zoneType: 'perimeter', label: 'Perimeter', description: 'Outer boundary crowd monitoring.', icon: 'scan-line', sortOrder: 90, defaultThresholds: { surge: 30, clusterSize: 20, dwellMinutes: 10 }, rules: [
      { alertType: 'restricted_zone_gathering', thresholds: {}, priority: 'high' },
      { alertType: 'sustained_loitering_cluster', thresholds: { personCount: 20, dwellMinutes: 10 }, priority: 'high' },
      { alertType: 'crowd_surge', thresholds: { surgeRatePct: 30, timeWindowMin: 5 }, priority: 'high' },
    ] },
    { zoneType: 'temporary_ops', label: 'Temporary Ops Zone', description: 'Temporary operation-scoped monitoring zone.', icon: 'clock', sortOrder: 100, defaultThresholds: {}, rules: [] },
  ],
};

// ─── MOCK_ZONES ───────────────────────────────────────────────────────────────
// MockZone extends Zone with a short display code (not part of @vigilo/shared).
// zoneCode is used in ZoneCard badges ("MGE", "NST", etc.).

interface MockZone extends Zone {
  zoneCode: string;
}

export const MOCK_ZONES: MockZone[] = [
  {
    id:           ZONE_ID.MGE,
    zoneCode:     'MGE',
    name:         'Main Entrance Gate',
    type:         'entry_exit_gate',
    status:       'warning',
    riskLevel:    'high',
    capacity:     8000,
    assignedTeam: 'Alpha Team',
    venueId:      VENUE_ID.ARENA,
    temporary:    false,
    activatedAt:  ago(92 * MIN),
    updatedAt:    ago(2 * MIN),
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [4.9404, 52.3127], [4.9426, 52.3127],
        [4.9426, 52.3132], [4.9404, 52.3132],
        [4.9404, 52.3127],
      ]],
    },
  },
  {
    id:           ZONE_ID.NST,
    zoneCode:     'NST',
    name:         'North Stand',
    type:         'seated_section',
    status:       'alert',
    riskLevel:    'high',
    capacity:     14500,
    assignedTeam: 'Bravo Team',
    venueId:      VENUE_ID.ARENA,
    temporary:    false,
    activatedAt:  ago(92 * MIN),
    updatedAt:    ago(1 * MIN),
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [4.9400, 52.3149], [4.9430, 52.3149],
        [4.9430, 52.3159], [4.9400, 52.3159],
        [4.9400, 52.3149],
      ]],
    },
  },
  {
    id:           ZONE_ID.SST,
    zoneCode:     'SST',
    name:         'South Stand',
    type:         'seated_section',
    status:       'normal',
    riskLevel:    'medium',
    capacity:     14500,
    assignedTeam: 'Charlie Team',
    venueId:      VENUE_ID.ARENA,
    temporary:    false,
    activatedAt:  ago(92 * MIN),
    updatedAt:    ago(3 * MIN),
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [4.9400, 52.3132], [4.9430, 52.3132],
        [4.9430, 52.3142], [4.9400, 52.3142],
        [4.9400, 52.3132],
      ]],
    },
  },
  {
    id:           ZONE_ID.MRC,
    zoneCode:     'MRC',
    name:         'Merchandise Area',
    type:         'service_area',
    status:       'warning',
    riskLevel:    'low',
    capacity:     3000,
    assignedTeam: 'Alpha Team',
    venueId:      VENUE_ID.ARENA,
    temporary:    false,
    activatedAt:  ago(92 * MIN),
    updatedAt:    ago(4 * MIN),
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [4.9435, 52.3132], [4.9450, 52.3132],
        [4.9450, 52.3149], [4.9435, 52.3149],
        [4.9435, 52.3132],
      ]],
    },
  },
  {
    id:           ZONE_ID.EEC,
    zoneCode:     'EEC',
    name:         'Emergency Exit Corridor',
    type:         'circulation_corridor',
    status:       'normal',
    riskLevel:    'critical',
    capacity:     2000,
    assignedTeam: null,
    venueId:      VENUE_ID.ARENA,
    temporary:    false,
    activatedAt:  ago(92 * MIN),
    updatedAt:    ago(5 * MIN),
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [4.9430, 52.3135], [4.9435, 52.3135],
        [4.9435, 52.3147], [4.9430, 52.3147],
        [4.9430, 52.3135],
      ]],
    },
  },
  {
    id:           ZONE_ID.VIP,
    zoneCode:     'VIP',
    name:         'VIP Area',
    type:         'vip_area',
    status:       'alert',
    riskLevel:    'critical',
    capacity:     500,
    assignedTeam: 'Command',
    venueId:      VENUE_ID.ARENA,
    temporary:    false,
    activatedAt:  ago(92 * MIN),
    updatedAt:    ago(1 * MIN),
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [4.9388, 52.3135], [4.9395, 52.3135],
        [4.9395, 52.3147], [4.9388, 52.3147],
        [4.9388, 52.3135],
      ]],
    },
  },
];

// ─── MOCK_DENSITY ─────────────────────────────────────────────────────────────

export const MOCK_DENSITY: Record<string, DensitySnapshot> = {
  [ZONE_ID.MGE]: {
    zoneId:              ZONE_ID.MGE,
    occupancyCount:      6320,
    densityScore:        79,
    capacityUtilisation: 79,
    trendDirection:      'rising',
    trendRate:           2.1,
    timestamp:           ago(10_000),
  },
  [ZONE_ID.NST]: {
    zoneId:              ZONE_ID.NST,
    occupancyCount:      12760,
    densityScore:        88,
    capacityUtilisation: 88,
    trendDirection:      'rising',
    trendRate:           3.4,
    timestamp:           ago(8_000),
  },
  [ZONE_ID.SST]: {
    zoneId:              ZONE_ID.SST,
    occupancyCount:      6525,
    densityScore:        45,
    capacityUtilisation: 45,
    trendDirection:      'stable',
    trendRate:           0.2,
    timestamp:           ago(12_000),
  },
  [ZONE_ID.MRC]: {
    zoneId:              ZONE_ID.MRC,
    occupancyCount:      2160,
    densityScore:        72,
    capacityUtilisation: 72,
    trendDirection:      'rising',
    trendRate:           1.8,
    timestamp:           ago(9_000),
  },
  [ZONE_ID.EEC]: {
    zoneId:              ZONE_ID.EEC,
    occupancyCount:      180,
    densityScore:        9,
    capacityUtilisation: 9,
    trendDirection:      'stable',
    trendRate:           0.0,
    timestamp:           ago(11_000),
  },
  [ZONE_ID.VIP]: {
    zoneId:              ZONE_ID.VIP,
    occupancyCount:      172,
    densityScore:        34,
    capacityUtilisation: 34,
    trendDirection:      'rising',
    trendRate:           1.2,
    timestamp:           ago(7_000),
  },
};

// ─── MOCK_ALERTS ──────────────────────────────────────────────────────────────

export const MOCK_ALERTS: Alert[] = [
  {
    id:             'alt-2461',
    operationId:    MOCK_OPERATIONS[0].id,
    zoneId:         ZONE_ID.NST,
    zoneName:       'North Stand',
    alertType:      'overcrowding',
    severity:       'critical',
    status:         'open',
    triggeredAt:    ago(4 * MIN),
    acknowledgedAt: null,
    escalatedAt:    null,
    resolvedAt:     null,
    acknowledgedBy: null,
    resolvedBy:     null,
    resolutionNote: null,
    triggerContext: {
      condition:      'Occupancy 88% > 85% threshold',
      currentValue:   88,
      thresholdValue: 85,
      unit:           '%',
    },
    zoneSnapshot: {
      densityScore:        88,
      occupancyCount:      12760,
      capacityUtilisation: 88,
      surgeRate:           3.4,
    },
  },
  {
    id:             'alt-2460',
    operationId:    MOCK_OPERATIONS[0].id,
    zoneId:         ZONE_ID.MGE,
    zoneName:       'Main Entrance Gate',
    alertType:      'crowd_surge',
    severity:       'high',
    status:         'acknowledged',
    triggeredAt:    ago(7 * MIN),
    acknowledgedAt: ago(5 * MIN),
    escalatedAt:    null,
    resolvedAt:     null,
    acknowledgedBy: UID.reyes,
    resolvedBy:     null,
    resolutionNote: null,
    triggerContext: {
      condition:      'Surge 18%/min in 4 min approaching 20% threshold',
      currentValue:   18,
      thresholdValue: 20,
      unit:           '%/min',
    },
    zoneSnapshot: {
      densityScore:        75,
      occupancyCount:      6000,
      capacityUtilisation: 75,
      surgeRate:           18.0,
    },
  },
  {
    id:             'alt-2459',
    operationId:    MOCK_OPERATIONS[0].id,
    zoneId:         ZONE_ID.VIP,
    zoneName:       'VIP Area',
    alertType:      'restricted_zone_gathering',
    severity:       'high',
    status:         'escalated',
    triggeredAt:    ago(18 * MIN),
    acknowledgedAt: ago(16 * MIN),
    escalatedAt:    ago(14 * MIN),
    resolvedAt:     null,
    acknowledgedBy: UID.reyes,
    resolvedBy:     null,
    resolutionNote: null,
    triggerContext: {
      condition:      '70+ persons inside restricted perimeter',
      currentValue:   70,
      thresholdValue: 0,
      unit:           'persons',
    },
    zoneSnapshot: {
      densityScore:        34,
      occupancyCount:      172,
      capacityUtilisation: 34,
      surgeRate:           1.2,
    },
  },
  {
    id:             'alt-2458',
    operationId:    MOCK_OPERATIONS[0].id,
    zoneId:         ZONE_ID.MRC,
    zoneName:       'Merchandise Area',
    alertType:      'zone_capacity_warning',
    severity:       'medium',
    status:         'open',
    triggeredAt:    ago(11 * MIN),
    acknowledgedAt: null,
    escalatedAt:    null,
    resolvedAt:     null,
    acknowledgedBy: null,
    resolvedBy:     null,
    resolutionNote: null,
    triggerContext: {
      condition:      'Occupancy 72% > 70% warning threshold',
      currentValue:   72,
      thresholdValue: 70,
      unit:           '%',
    },
    zoneSnapshot: {
      densityScore:        72,
      occupancyCount:      2160,
      capacityUtilisation: 72,
      surgeRate:           1.8,
    },
  },
  {
    id:             'alt-2457',
    operationId:    MOCK_OPERATIONS[0].id,
    zoneId:         ZONE_ID.SST,
    zoneName:       'South Stand',
    alertType:      'zone_capacity_warning',
    severity:       'medium',
    status:         'resolved',
    triggeredAt:    ago(45 * MIN),
    acknowledgedAt: ago(43 * MIN),
    escalatedAt:    null,
    resolvedAt:     ago(38 * MIN),
    acknowledgedBy: UID.reyes,
    resolvedBy:     UID.halberg,
    resolutionNote: 'Crowd dispersed naturally after half-time',
    triggerContext: {
      condition:      'Occupancy briefly exceeded 70% threshold',
      currentValue:   71,
      thresholdValue: 70,
      unit:           '%',
    },
    zoneSnapshot: {
      densityScore:        71,
      occupancyCount:      10295,
      capacityUtilisation: 71,
      surgeRate:           0.5,
    },
  },
];

// ─── MOCK_FLAGS ───────────────────────────────────────────────────────────────

const FLAG_SESSION = 'session-vigilo-demo-2026';

export const MOCK_FLAGS: FlagRecord[] = [
  {
    flagId:           'flag-001',
    sessionId:        FLAG_SESSION,
    tokenRef:         'f1a2b3c4-d5e6-4007-8000-0000000a1f70',
    priority:         'urgent',
    behaviourNote:    'Repeated restricted zone entry — 3 visits to VIP in 40 min',
    monitoredZoneIds: [ZONE_ID.VIP, ZONE_ID.NST, ZONE_ID.MGE],
    createdAt:        ago(40 * MIN),
    expiresAt:        null,
    createdBy:        UID.reyes,
  },
  {
    flagId:           'flag-002',
    sessionId:        FLAG_SESSION,
    tokenRef:         'f1a2b3c4-d5e6-4007-8000-0000000b2c40',
    priority:         'watch',
    behaviourNote:    'Dwell anomaly — Gate 3 area, stationary 23 min',
    monitoredZoneIds: [ZONE_ID.MGE, ZONE_ID.EEC],
    createdAt:        ago(30 * MIN),
    expiresAt:        null,
    createdBy:        UID.reyes,
  },
];

// ─── MOCK_FLAG_ACTIVITY ───────────────────────────────────────────────────────

export const MOCK_FLAG_ACTIVITY: Record<string, FlagActivity> = {
  'flag-001': {
    flagId:   'flag-001',
    tokenRef: 'f1a2b3c4-d5e6-4007-8000-0000000a1f70',
    zonesVisited: [
      { zoneId: ZONE_ID.MGE, zoneName: 'Main Entrance Gate', entryAt: ago(42 * MIN), exitAt: ago(38 * MIN), dwellMinutes: 4  },
      { zoneId: ZONE_ID.VIP, zoneName: 'VIP Area',           entryAt: ago(38 * MIN), exitAt: ago(32 * MIN), dwellMinutes: 6  },
      { zoneId: ZONE_ID.NST, zoneName: 'North Stand',        entryAt: ago(32 * MIN), exitAt: ago(24 * MIN), dwellMinutes: 8  },
      { zoneId: ZONE_ID.VIP, zoneName: 'VIP Area',           entryAt: ago(24 * MIN), exitAt: ago(16 * MIN), dwellMinutes: 8  },
      { zoneId: ZONE_ID.VIP, zoneName: 'VIP Area',           entryAt: ago(12 * MIN), exitAt: null,          dwellMinutes: 12 },
    ],
    currentZoneId: ZONE_ID.VIP,
    alertEvents: [
      { zoneId: ZONE_ID.VIP, eventType: 'entry',   timestamp: ago(38 * MIN) },
      { zoneId: ZONE_ID.VIP, eventType: 'reentry', timestamp: ago(24 * MIN) },
      { zoneId: ZONE_ID.VIP, eventType: 'reentry', timestamp: ago(12 * MIN) },
    ],
  },
  'flag-002': {
    flagId:   'flag-002',
    tokenRef: 'f1a2b3c4-d5e6-4007-8000-0000000b2c40',
    zonesVisited: [
      { zoneId: ZONE_ID.MGE, zoneName: 'Main Entrance Gate',    entryAt: ago(32 * MIN), exitAt: ago(26 * MIN), dwellMinutes: 6  },
      { zoneId: ZONE_ID.EEC, zoneName: 'Emergency Exit Corridor', entryAt: ago(26 * MIN), exitAt: ago(18 * MIN), dwellMinutes: 8  },
      { zoneId: ZONE_ID.MGE, zoneName: 'Main Entrance Gate',    entryAt: ago(18 * MIN), exitAt: null,          dwellMinutes: 18 },
    ],
    currentZoneId: ZONE_ID.MGE,
    alertEvents: [
      { zoneId: ZONE_ID.EEC, eventType: 'entry',        timestamp: ago(26 * MIN) },
      { zoneId: ZONE_ID.EEC, eventType: 'dwell_exceeded', timestamp: ago(18 * MIN) },
    ],
  },
};

// ─── MOCK_DASHBOARD ───────────────────────────────────────────────────────────

const NOW_ISO = new Date(NOW).toISOString();

type MockZoneStatusCard = ZoneStatusCard & { capacity: number; occupancyCount: number | null };
type MockCommandDashboardFlagRow = {
  flagId: string;
  tokenRef: string;
  priority: FlagRecord['priority'];
  behaviourNote: string;
  monitoredZoneIds: string[];
  createdAt: string;
  expiresAt: string | null;
  currentZoneId: string | null;
  currentZoneName: string | null;
  latestActivityAt: string | null;
  activityStatus: 'active_in_zone' | 'recent_activity' | 'no_activity';
};

const ZONE_STATUS_CARDS: MockZoneStatusCard[] = MOCK_ZONES.map(z => {
  const snap = MOCK_DENSITY[z.id];
  const alertCount = MOCK_ALERTS.filter(
    a => a.zoneId === z.id && a.status !== 'resolved'
  ).length;
  return {
    zoneId:         z.id,
    zoneName:       z.name,
    zoneType:       z.type,
    capacity:       z.capacity,
    status:         z.status,
    occupancyCount: snap?.occupancyCount ?? null,
    occupancyPct:   snap?.capacityUtilisation ?? 0,
    alertCount,
    trendDirection: snap?.trendDirection ?? 'stable',
    lastUpdated:    snap?.timestamp ?? NOW_ISO,
  };
});

const MOCK_TOTAL_CAPACITY = MOCK_ZONES.reduce((sum, zone) => sum + zone.capacity, 0);
const MOCK_TOTAL_OCCUPANCY = ZONE_STATUS_CARDS.reduce((sum, card) => sum + (card.occupancyCount ?? 0), 0);
const MOCK_CROWD_LOAD_INDEX = MOCK_TOTAL_CAPACITY > 0
  ? Math.round((MOCK_TOTAL_OCCUPANCY / MOCK_TOTAL_CAPACITY) * 100)
  : 0;
const MOCK_CROWD_LOAD_TREND = Array.from({ length: 12 }, (_, i) => ({
  bucketStart: new Date(NOW - (11 - i) * 5 * MIN).toISOString(),
  crowdLoadIndex: Math.max(0, Math.min(100, MOCK_CROWD_LOAD_INDEX - Math.round((11 - i) * 1.5))),
}));

function latestIso(values: Array<string | null | undefined>): string | null {
  const sorted = values.filter((value): value is string => Boolean(value)).sort();
  return sorted[sorted.length - 1] ?? null;
}

const MOCK_FLAG_ROWS: MockCommandDashboardFlagRow[] = MOCK_FLAGS.map(flag => {
  const activity = MOCK_FLAG_ACTIVITY[flag.flagId];
  const latestVisitAt = latestIso(activity?.zonesVisited.flatMap(visit => [visit.entryAt, visit.exitAt]) ?? []);
  const latestAlertAt = latestIso(activity?.alertEvents.map(event => event.timestamp) ?? []);
  const latestActivityAt = latestIso([latestVisitAt, latestAlertAt]);
  const currentZone = MOCK_ZONES.find(zone => zone.id === activity?.currentZoneId);
  return {
    flagId: flag.flagId,
    tokenRef: flag.tokenRef,
    priority: flag.priority,
    behaviourNote: flag.behaviourNote,
    monitoredZoneIds: flag.monitoredZoneIds,
    createdAt: flag.createdAt,
    expiresAt: flag.expiresAt,
    currentZoneId: activity?.currentZoneId ?? null,
    currentZoneName: currentZone?.name ?? null,
    latestActivityAt,
    activityStatus: activity?.currentZoneId ? 'active_in_zone' : latestActivityAt ? 'recent_activity' : 'no_activity',
  };
});

export const MOCK_DASHBOARD: CommandDashboard & {
  crowdLoadTrend: {
    windowMinutes: number;
    buckets: typeof MOCK_CROWD_LOAD_TREND;
  };
  zoneStatusCards: MockZoneStatusCard[];
  flagSummary: {
    active: number;
    urgent: number;
    limit: number;
  };
  flagRows: MockCommandDashboardFlagRow[];
} = {
  generatedAt: NOW_ISO,
  kpis: {
    totalZones:     6,
    zonesInAlert:   2,   // NST, VIP
    zonesInWarning: 2,   // MGE, MRC
    zonesNormal:    2,   // SST, EEC
    activeAlerts:   4,   // open + acknowledged + escalated (excluding resolved)
    criticalAlerts: 1,   // ALT-2461
    crowdLoadIndex: MOCK_CROWD_LOAD_INDEX,
    highestOccupancyZone: {
      zoneId:      ZONE_ID.NST,
      zoneName:    'North Stand',
      utilisation: 88,
    },
  },
  flagSummary: {
    active: MOCK_FLAG_ROWS.length,
    urgent: MOCK_FLAG_ROWS.filter(flag => flag.priority === 'urgent').length,
    limit: 20,
  },
  flagRows: MOCK_FLAG_ROWS,
  crowdLoadTrend: {
    windowMinutes: 60,
    buckets: MOCK_CROWD_LOAD_TREND,
  },
  zoneStatusCards: ZONE_STATUS_CARDS,
  movementTrend: {
    windowMinutes: 60,
    totalInflow:   3240,
    totalOutflow:  1820,
    netFlow:       1420,
    byZone: [
      { zoneId: ZONE_ID.MGE, inflow: 1420, outflow:  880 },
      { zoneId: ZONE_ID.NST, inflow: 1080, outflow:  420 },
      { zoneId: ZONE_ID.SST, inflow:  360, outflow:  840 },
      { zoneId: ZONE_ID.MRC, inflow:  280, outflow:  210 },
      { zoneId: ZONE_ID.EEC, inflow:   60, outflow:  340 },
      { zoneId: ZONE_ID.VIP, inflow:   40, outflow:  130 },
    ],
  },
};

// ─── MOCK_USERS ───────────────────────────────────────────────────────────────

export const MOCK_USERS: User[] = [
  {
    id:          UID.reyes,
    email:       'analyst@vigilo.dev',
    displayName: 'S. Reyes',
    role:        'crowd_analyst',
    team:        'Alpha Team',
    orgId:       'org-vigilo',
    active:      true,
    lastSeen:    ago(30_000),
  },
  {
    id:          UID.patel,
    email:       'analyst2@vigilo.dev',
    displayName: 'K. Patel',
    role:        'crowd_analyst',
    team:        'Bravo Team',
    orgId:       'org-vigilo',
    active:      true,
    lastSeen:    ago(2 * MIN),
  },
  {
    id:          UID.chen,
    email:       'analyst3@vigilo.dev',
    displayName: 'L. Chen',
    role:        'crowd_analyst',
    team:        'Charlie Team',
    orgId:       'org-vigilo',
    active:      true,
    lastSeen:    ago(5 * MIN),
  },
  {
    id:          UID.halberg,
    email:       'supervisor@vigilo.dev',
    displayName: 'M. Halberg',
    role:        'command_supervisor',
    team:        'Command',
    orgId:       'org-vigilo',
    active:      true,
    lastSeen:    ago(45_000),
  },
  {
    id:          UID.ostrowski,
    email:       'operator@vigilo.dev',
    displayName: 'R. Ostrowski',
    role:        'system_admin',
    team:        'Operations',
    orgId:       'org-vigilo',
    active:      true,
    lastSeen:    ago(14 * MIN),
  },
];

// ─── MOCK_HISTORY ─────────────────────────────────────────────────────────────
// 24-hour density history per zone — 288 ticks at 5-minute resolution.
// Tick 0 = midnight of the mock operation day.
// Tick 200 = 16:40 — anchored as the mock "live now" point;
//   values at tick 200 match MOCK_DENSITY (NST 88%, MGE 79%, etc.).
// Special events: MGE surge peak at tick 200, VIP restricted gathering from tick 240.

export interface DensityDataPoint {
  timestamp:           string;  // ISO, one per 5-minute tick
  capacityUtilisation: number;  // 0–100 %
  occupancyCount:      number;
}

export const HIST_TICK_MS  = 5 * 60_000;  // 5 minutes in ms
export const HIST_NOW_TICK = 200;         // tick corresponding to mock "now" (16:40)

// Midnight of the mock day, derived from NOW so dates stay consistent.
export const HIST_DAY_MS = (() => {
  const d = new Date(NOW);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
})();

// Base venue crowd curve — returns approx utilisation 0–100 for a given tick.
// Peak band (ticks 192–216 = 16:00–18:00) sits around 82 %.
function eventCurve(t: number): number {
  const h = t / 12;
  if (h < 9)  return 8  + Math.sin(t * 0.80) * 1.2;
  if (h < 12) return 8  + (h - 9)  * 5   + Math.sin(t * 0.60) * 1.5;
  if (h < 14) return 23 + (h - 12) * 15  + Math.sin(t * 0.50) * 2.0;
  if (h < 16) return 53 + (h - 14) * 14  + Math.sin(t * 0.50) * 2.5;
  if (h < 18) return 81 + Math.sin(t * 0.35) * 3.5;
  if (h < 20) return 81 - (h - 18) * 17  + Math.sin(t * 0.40) * 3.0;
  if (h < 22) return 47 - (h - 20) * 14  + Math.sin(t * 0.50) * 2.0;
  return Math.max(5, 19 - (h - 22) * 5   + Math.sin(t * 0.70) * 1.0);
}

function clampUtil(v: number): number {
  return Math.min(100, Math.max(0, Math.round(v * 10) / 10));
}

function buildZoneHistory(id: string, capacity: number): DensityDataPoint[] {
  return Array.from({ length: 288 }, (_, t) => {
    const base = eventCurve(t);
    const h    = t / 12;
    let v      = base;

    if (id === ZONE_ID.NST) {
      // Largest stand — consistently high; hits 88% at tick 200
      v = base * 1.06 + Math.sin(t * 0.28) * 2;
    } else if (id === ZONE_ID.MGE) {
      // Entry gate — surge spike centred at tick 200, eases once fans are seated
      v = base * 0.96;
      if (t >= 196 && t <= 212) v += 8 * Math.sin(((t - 196) / 16) * Math.PI);
      if (h >= 17) v *= 0.87;
    } else if (id === ZONE_ID.SST) {
      // South stand — lower fill rate; 45% at tick 200
      v = base * 0.57 + Math.sin(t * 0.42) * 2;
    } else if (id === ZONE_ID.MRC) {
      // Merchandise — pre-match shopping rush; 72% at tick 200
      v = base * 0.88 + Math.sin(t * 0.46) * 2;
      if (h >= 13 && h < 15) v += 5;
    } else if (id === ZONE_ID.EEC) {
      // Emergency exit corridor — always near-empty; spike at dispersal
      if (h < 16)       v = 4  + Math.sin(t * 0.90) * 2;
      else if (h < 20)  v = 4  + (h - 16) * 1.5 + Math.sin(t * 0.70) * 1.5;
      else              v = Math.max(4, 10 - (h - 20) * 2 + Math.sin(t * 0.80) * 1);
    } else if (id === ZONE_ID.VIP) {
      // Restricted area — low normally; restricted gathering builds from tick 240 (20:00)
      v = base * 0.38 + Math.sin(t * 0.52) * 2;
      if (t >= 240) v = Math.min(62, v + (t - 240) * 0.35);
    }

    const util = clampUtil(v);
    return {
      timestamp:           new Date(HIST_DAY_MS + t * HIST_TICK_MS).toISOString(),
      capacityUtilisation: util,
      occupancyCount:      Math.round(capacity * util / 100),
    };
  });
}

export const MOCK_HISTORY: Record<string, DensityDataPoint[]> = Object.fromEntries(
  MOCK_ZONES.map(z => [z.id, buildZoneHistory(z.id, z.capacity)])
);

// ─── MOCK_REPORT_TEMPLATES ────────────────────────────────────────────────────

export interface ReportTemplate {
  id:          string;
  name:        string;
  description: string;
  format:      'pdf' | 'csv' | 'xlsx';
  category:    'zone' | 'alert' | 'movement' | 'flag' | 'operational';
}

export const MOCK_REPORT_TEMPLATES: ReportTemplate[] = [
  { id: 'rpt-001', name: 'Zone Activity Summary',        description: 'Density, occupancy and trend data per zone over a time window',       format: 'pdf',  category: 'zone'        },
  { id: 'rpt-002', name: 'Alert Lifecycle Report',       description: 'Full alert history with acknowledgement and resolution timelines',     format: 'pdf',  category: 'alert'       },
  { id: 'rpt-003', name: 'Crowd Flow Analysis',          description: 'Entry/exit rates, net flow, and movement vectors across all zones',   format: 'pdf',  category: 'movement'    },
  { id: 'rpt-004', name: 'Anonymous Flag Summary',       description: 'Flagged token activity log — no PII, behaviour notes only',           format: 'pdf',  category: 'flag'        },
  { id: 'rpt-005', name: 'Operational Incident Log',     description: 'Chronological event log suitable for post-event debrief',             format: 'pdf',  category: 'operational' },
  { id: 'rpt-006', name: 'Zone Density Export (CSV)',    description: 'Raw density snapshot data for all zones, 1-minute resolution',        format: 'csv',  category: 'zone'        },
  { id: 'rpt-007', name: 'Alert Data Export (CSV)',      description: 'Alert records with trigger context for external analysis',            format: 'csv',  category: 'alert'       },
];

export type ReportJobStatus = 'queued' | 'generating' | 'ready' | 'failed';
export type ReportJobFormat = 'pdf' | 'csv';

export interface MockReportJob {
  id: string;
  templateId: string;
  format: ReportJobFormat;
  status: ReportJobStatus;
  requestedAt: string;
  completedAt: string | null;
  outputHash: string | null;
  outputSizeBytes: number | null;
  errorMessage: string | null;
  parameters: Record<string, unknown>;
  downloadUrl?: string;
}

export const MOCK_REPORT_JOBS: MockReportJob[] = [
  {
    id: 'rpt-job-1004',
    templateId: 'zone_activity_summary',
    format: 'pdf',
    status: 'ready',
    requestedAt: ago(10 * MIN),
    completedAt: ago(9 * MIN),
    outputHash: 'a4f2c9d7b81e44ad9e82c7bb6ab5d6cfb9a7188ed5ef3020ad8f6e9180d18b81',
    outputSizeBytes: 145230,
    errorMessage: null,
    parameters: {
      operationId: MOCK_OPERATIONS[0].id,
      zoneId: ZONE_ID.NST,
      from: ago(70 * MIN),
      to: ago(10 * MIN),
    },
    downloadUrl: '/api/v1/reports/rpt-job-1004/download',
  },
  {
    id: 'rpt-job-1003',
    templateId: 'alert_history_report',
    format: 'csv',
    status: 'ready',
    requestedAt: ago(25 * MIN),
    completedAt: ago(24 * MIN),
    outputHash: '9d31a67cef02f41b7ac270cd01ce47e1892a677c4995f600a84536f7b129ef02',
    outputSizeBytes: 28400,
    errorMessage: null,
    parameters: {
      operationId: MOCK_OPERATIONS[0].id,
      from: ago(24 * 60 * MIN),
      to: ago(25 * MIN),
    },
    downloadUrl: '/api/v1/reports/rpt-job-1003/download',
  },
  {
    id: 'rpt-job-1002',
    templateId: 'crowd_trend_report',
    format: 'pdf',
    status: 'failed',
    requestedAt: ago(35 * MIN),
    completedAt: ago(34 * MIN),
    outputHash: null,
    outputSizeBytes: null,
    errorMessage: 'Insufficient data for time range',
    parameters: {
      operationId: MOCK_OPERATIONS[0].id,
      from: ago(36 * MIN),
      to: ago(35 * MIN),
    },
  },
  {
    id: 'rpt-job-1001',
    templateId: 'operational_summary',
    format: 'pdf',
    status: 'queued',
    requestedAt: ago(30_000),
    completedAt: null,
    outputHash: null,
    outputSizeBytes: null,
    errorMessage: null,
    parameters: {
      operationId: MOCK_OPERATIONS[0].id,
      from: ago(6 * 60 * MIN),
      to: new Date(NOW).toISOString(),
    },
  },
];
